import CardGridView from "@/components/CardGridView";
import DeleteDialog from "@/components/DeleteDialog";
import { Loading } from "@/components/Loading";
import { useSiteName } from "@/hooks/useSiteName";
import type { LayoutContext } from "@/lib/models";
import { priorityProjectColors, stageProjectColors } from "@/lib/task-utils";
import { projectCreateUrlBuilder, projectEditUrlBuilder } from "@/lib/url-builder";
import { formatCurrenty, formatDateDisplayString } from "@/lib/string-utils";
import { CodeService, type Code } from "@/services/CodeService";
import { Project, ProjectService } from "@/services/ProjectService";
import { Calendar, CheckCircle, Flag, Pencil, PoundSterling, User } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router";

export interface ProjectListItem {
  id: string;
  name: string;
  priority: Code | undefined;
  stage: Code | undefined;
  cost: number;
  staffDays: number;
  startDate: string | null;
  endDate: string | null;
}

const SiteProjectListPage: React.FC = () => {
  const params = useParams();
  const { setPageName } = useOutletContext<LayoutContext>();
  const [projectList, setProjectList] = useState<ProjectListItem[]>([]);
  const [deletedProjectId, setDeletedProjectId] = useState<string | null>(null);
  const [showDeleteProjectDialog, setShowDeleteProjectDialog] = useState(false);
  const { siteName } = useSiteName(params.siteId);
  const [loading, setLoading] = useState(true);

  const mapToActionListItem = (
    project: Project,
    codes: Code[]
  ): ProjectListItem => {
    return {
      id: project.id,
      endDate: project.endDate,
      name: project.name,
      cost: project.cost,
      priority: getCode(project.priorityCodeId, codes),
      startDate: project.startDate,
      stage: getCode(project.stageCodeId, codes),
      staffDays: project.staffDays,
    };
  };

  const getCode = (codeId: string, codes: Code[]): Code | undefined => {
    return codes.find((c) => c.id == codeId);
  };

  useEffect(() => {
    const initializeActions = async () => {
      const codes = await CodeService.getCodes2();
      const projects = await ProjectService.getBySiteId(params.siteId!);
      const temp: ProjectListItem[] = [];
      projects.forEach((a) => {
        temp.push(mapToActionListItem(a, codes));
      });
      setProjectList(temp.sort((a, b) => a.name.localeCompare(b.name)));
      setLoading(false);
    };
    initializeActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPageName("Tasks List"); // Set the page name when the component mounts
    return () => setPageName("Tasks List"); // Optional: Reset on unmount
  }, [setPageName]);

  return (
    <>
      <div className="container mx-auto p-4">
        <Loading message="Loading projects..." isLoading={loading} />
        <CardGridView
          title={`Site: ${siteName}`}
          editPageUrl={(site: string, id: string) =>
            projectEditUrlBuilder(params.tenantId!, site, id)
          }
          createPageUrl={projectCreateUrlBuilder(params.tenantId!, params.siteId!)}
          items={projectList}
          onDeleteItem={async (project: ProjectListItem) => {
            setDeletedProjectId(project.id);
            setShowDeleteProjectDialog(true);
          }}
          renderContentItem={(project: ProjectListItem) => (
            <div className="space-y-1">
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Flag className="h-4 w-4 text-gray-400" />
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      priorityProjectColors[project.priority!.id]
                    }`}
                  >
                    {project.priority?.name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-gray-400" />
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      stageProjectColors[project.stage!.id]
                    }`}
                  >
                    {project.stage?.name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <PoundSterling className="h-4 w-4 text-gray-400" />
                  <span className="text-xs text-gray-500">
                    {formatCurrenty(project.cost)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-xs text-gray-500">
                    {project.staffDays}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-xs text-gray-500">
                    Start: {formatDateDisplayString(project.startDate ?? "")}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-xs text-gray-500">
                    End: {formatDateDisplayString(project.endDate ?? "")}
                  </span>
                </div>
                {/* <div className="flex items-center gap-2">
                  {project && (
                    <Badge variant="secondary" className="text-xs">
                      <Users className="h-3 w-3 mr-1" />
                      Assigned Name
                    </Badge>
                  )}
                </div> */}
              </div>
            </div>
          )}
          tableHeaders={[
            { id: "name", child: <>Name</> },
            {
              id: "description",
              child: (
                <>
                  <Flag className="inline-block mr-1 h-4 w-4" /> Priority
                </>
              ),
            },
            {
              id: "status",
              child: (
                <>
                  <CheckCircle className="inline-block mr-1 h-4 w-4" /> Status
                </>
              ),
            },
            {
              id: "cost",
              child: (
                <>
                  <PoundSterling className="inline-block mr-1 h-4 w-4" /> Cost
                </>
              ),
            },
            {
              id: "staffDays",
              child: (
                <>
                  <User className="inline-block mr-1 h-4 w-4" /> Staff Days
                </>
              ),
            },
            {
              id: "start",
              child: (
                <>
                  <Calendar className="inline-block mr-1 h-4 w-4" /> Start
                </>
              ),
            },
            {
              id: "end",
              child: (
                <>
                  <Calendar className="inline-block mr-1 h-4 w-4" /> End
                </>
              ),
            },
            { id: "actions", child: <>Edit</> },
          ]}
          tableContentItem={(project: ProjectListItem) => [
            <Link
              to={projectEditUrlBuilder(params.tenantId!, params.siteId!, project.id)}
              className="hover:text-blue-600 transition-colors duration-200 cursor-pointer"
            >
              {project.name}
            </Link>,
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                priorityProjectColors[project.priority!.id]
              }`}
            >
              {project.priority?.name}
            </span>,
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                stageProjectColors[project.stage!.id]
              }`}
            >
              {project.stage?.name}
            </span>,
            <>{formatCurrenty(project.cost)}</>,
            <>{project.staffDays}</>,
            <>{formatDateDisplayString(project.startDate ?? "")}</>,
            <>{formatDateDisplayString(project.endDate ?? "")}</>,
            <Link to={projectEditUrlBuilder(params.tenantId!, params.siteId!, project.id)}>
              <Pencil
                className="inline-block mr-1 h-4 w-4 hover:text-blue-600 transition-colors duration-200 cursor-pointer"
                aria-label={`Edit ${project.name}`}
              />
            </Link>,
          ]}
        />
      </div>
      <DeleteDialog
        onDelete={async () => {
          if (!deletedProjectId) {
            alert("No feature selected for deletion");
            return;
          }
          await ProjectService.delete(deletedProjectId);
          setProjectList(projectList.filter((a) => a.id !== deletedProjectId));
          setDeletedProjectId(null);
        }}
        deleteConfirmationText={
          <p>Are you sure you want to delete this project?</p>
        }
        isOpen={showDeleteProjectDialog}
        onOpenChange={setShowDeleteProjectDialog}
      />
    </>
  );
};

export default SiteProjectListPage;
